import { racePromiseWithAbortSignal, raceWithTimeout } from "@openclaw/retry";
import type {
  PluginControlUiDiagnostic,
  PluginControlUiModule,
  PluginsControlUiCatalog,
} from "../../../packages/gateway-protocol/src/schema/plugins.js";
import { CONTROL_UI_PLUGIN_AUTH_GRANT_TTL_MS } from "../../../src/gateway/control-ui-plugin-frame-contract.js";
import type {
  ControlUiSettingsPlacement,
  ControlUiChannelSettingsView,
  ControlUiSettingsView,
} from "../../../src/plugin-sdk/control-ui-settings.js";
import type {
  ControlUiDisposer,
  ControlUiHost,
  ControlUiReplacement,
  ControlUiSurface,
} from "../../../src/plugin-sdk/control-ui.js";
import type { GatewayBrowserClient } from "../api/gateway.ts";
import type { ApplicationContext } from "../app/context.ts";
import { readGatewayOperatorAccess } from "../app/operator-access.ts";
import { hasSameOriginGatewayTransport } from "../dev-gateway.ts";
import { formatUiError } from "../lib/format-error.ts";
import { isGatewayMethodAdvertised } from "../lib/gateway-methods.ts";
import { listen } from "../lib/reactive/listener.ts";
import {
  controlUiPluginAssetGrantError,
  controlUiPluginDeliveryIdentity,
  controlUiPluginDistribution,
  ControlUiPluginAssetPreloads,
} from "./control-ui-assets.ts";
import type {
  ControlUiContributions,
  ControlUiPluginCapability,
  ControlUiRegistration,
} from "./control-ui-capability.ts";
import { controlUiPluginDistributions } from "./control-ui-distribution.ts";

export type ControlUiPluginOwner = {
  descriptor: PluginControlUiModule;
  deliveryIdentity?: string;
  client: GatewayBrowserClient;
  abort: AbortController;
  disposers: Set<ControlUiDisposer>;
  contributions: {
    [K in keyof ControlUiContributions]: Map<
      string,
      { value: ControlUiContributions[K]; signal: AbortSignal }
    >;
  };
  selections: Map<ControlUiSurface, string | null>;
  host: ControlUiHost;
};

const UI_CAPABILITY_BY_CONTRIBUTION = {
  pages: "page",
  navigation: "navigation",
  panels: "panel",
  actions: "action",
  accessories: "accessory",
  widgets: "widget",
  replacements: "replacement",
} as const satisfies Record<
  keyof ControlUiContributions,
  import("../../../packages/gateway-protocol/src/plugin-ui-capabilities.ts").PluginUiCapability
>;

const CONTRIBUTION_ID = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/;
const ACTIVATION_TIMEOUT_MS = 15_000;

function sameSettingsPlacement(a: ControlUiSettingsPlacement, b: ControlUiSettingsPlacement) {
  return (
    a.section === b.section &&
    a.panel === b.panel &&
    (a.section !== "channels" || (b.section === "channels" && a.channelId === b.channelId))
  );
}

export class ControlUiPluginRuntime implements ControlUiPluginCapability {
  private readonly owners = new Map<string, ControlUiPluginOwner>();
  private readonly preloads = new ControlUiPluginAssetPreloads();
  private readonly selected = new Map<ControlUiSurface, { key: string; signal: AbortSignal }>();
  private readonly listeners = new Set<() => void>();
  private readonly loadingOwners = new Set<Omit<ControlUiPluginOwner, "host">>();
  private loadingCatalog: "pending" | Set<string> | null = null;
  private catalogStatus: "pending" | "complete" | "failed" = "pending";
  private readonly stops: ControlUiDisposer[] = [];
  private client: GatewayBrowserClient | null = null;
  private connectionId: string | null = null;
  private refreshGeneration = 0;
  private disposed = false;
  private readonly settingsLifetimes = new Set<AbortController>();
  private firstConnection = true;
  private diagnostics: PluginControlUiDiagnostic[] = [];
  private grantTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly getContext: () => ApplicationContext) {}

  get errors(): readonly PluginControlUiDiagnostic[] {
    // Warnings belong to live registrations, so ordinary catalog refreshes retain
    // them and retiring a contribution removes them without hiding activation errors.
    const warnings = [...this.owners.values(), ...this.loadingOwners].flatMap((owner) =>
      // SAFETY: the canonical mapping satisfies exactly the contribution registry keys.
      (Object.keys(UI_CAPABILITY_BY_CONTRIBUTION) as (keyof ControlUiContributions)[])
        .filter(
          (kind) =>
            owner.contributions[kind].size > 0 &&
            owner.descriptor.uiCapabilities &&
            !owner.descriptor.uiCapabilities.includes(UI_CAPABILITY_BY_CONTRIBUTION[kind]),
        )
        .map((kind) => ({
          pluginId: owner.descriptor.pluginId,
          message: `Registered UI capability "${UI_CAPABILITY_BY_CONTRIBUTION[kind]}" is missing from uiCapabilities in openclaw.plugin.json.`,
        })),
    );
    return [...this.diagnostics, ...warnings];
  }

  loadSettingsView(
    placement: ControlUiSettingsView["placement"],
    signal: AbortSignal,
  ): Promise<{ view: ControlUiSettingsView; signal: AbortSignal; dispose: () => void }>;
  loadSettingsView(
    placement: ControlUiChannelSettingsView["placement"],
    signal: AbortSignal,
  ): Promise<{ view: ControlUiChannelSettingsView; signal: AbortSignal; dispose: () => void }>;
  async loadSettingsView(placement: ControlUiSettingsPlacement, signal: AbortSignal) {
    if (this.disposed) {
      throw new Error("The plugin UI runtime has ended.");
    }
    signal.throwIfAborted();
    const matches = controlUiPluginDistributions.filter((entry) =>
      entry.settingsViews?.some((item) => sameSettingsPlacement(item, placement)),
    );
    if (matches.length !== 1) {
      throw new Error("Settings presentation requires one packaged owner.");
    }
    const distribution = matches[0]!;
    const abort = new AbortController();
    const lifetime = AbortSignal.any([signal, abort.signal]);
    const disposers = new Set<() => void>();
    let retired = false;
    const dispose = () => {
      if (retired) {
        return;
      }
      retired = true;
      abort.abort();
      for (const stop of disposers) {
        stop();
      }
      disposers.clear();
      this.settingsLifetimes.delete(abort);
    };
    this.settingsLifetimes.add(abort);
    disposers.add(listen(lifetime, "abort", dispose, { once: true }));
    const styles: HTMLLinkElement[] = [];
    try {
      const { loadControlUiPluginAssets } = await import("./control-ui-loader.ts");
      const module = await raceWithTimeout(
        loadControlUiPluginAssets(
          distribution.pluginId,
          distribution,
          lifetime,
          (stop) => disposers.add(stop),
          styles,
        ),
        ACTIVATION_TIMEOUT_MS,
        () => {
          throw new Error("Settings presentation timed out.");
        },
      );
      lifetime.throwIfAborted();
      if (
        module.pluginId !== distribution.pluginId ||
        !Array.isArray(module.settingsViews) ||
        module.settingsViews.length !== distribution.settingsViews?.length
      ) {
        throw new Error("Settings presentation does not match its packaged owner and placements.");
      }
      const declared = module.settingsViews.every(
        (view) =>
          typeof view.mount === "function" &&
          distribution.settingsViews?.some((item) => sameSettingsPlacement(item, view.placement)),
      );
      const views = module.settingsViews.filter((view) =>
        sameSettingsPlacement(view.placement, placement),
      );
      const view = views[0];
      if (!declared || views.length !== 1 || !view) {
        throw new Error("Invalid or duplicate Settings presentation.");
      }
      for (const style of styles) {
        style.media = "all";
      }
      return { view, signal: lifetime, dispose };
    } catch (error) {
      dispose();
      throw error;
    }
  }

  get hasPlugins(): boolean {
    return this.owners.size > 0 || this.loadingOwners.size > 0;
  }

  get registryStatus(): "pending" | "complete" | "failed" {
    if (this.catalogStatus === "failed") {
      return "failed";
    }
    return this.catalogStatus === "complete" &&
      this.loadingCatalog === null &&
      this.loadingOwners.size === 0
      ? "complete"
      : "pending";
  }

  isLoading(pluginId: string): boolean {
    return (
      this.loadingCatalog === "pending" ||
      this.loadingCatalog?.has(pluginId) === true ||
      [...this.loadingOwners].some((owner) => owner.descriptor.pluginId === pluginId)
    );
  }

  get canReload(): boolean {
    const snapshot = this.getContext().gateway.snapshot;
    return (
      !this.disposed &&
      snapshot.phase === "connected" &&
      readGatewayOperatorAccess(snapshot).canAdmin &&
      isGatewayMethodAdvertised(snapshot, "plugins.controlUi.reload") === true
    );
  }

  async reload(): Promise<void> {
    if (!this.canReload || !this.client) {
      throw new Error("Reloading plugin UI requires a connected operator with admin access.");
    }
    const client = this.client;
    const connectionId = this.connectionId;
    await client.request("plugins.controlUi.reload", {});
    if (this.disposed || this.client !== client || this.connectionId !== connectionId) {
      throw new Error("The connection changed while reloading plugin UI. Reconnect and retry.");
    }
    await this.refresh();
  }

  subscribe(listener: () => void): ControlUiDisposer {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  invalidate(owner: Omit<ControlUiPluginOwner, "host">): void {
    if (!this.isCurrent(owner)) {
      throw new Error("This plugin UI activation has ended.");
    }
    this.publish();
  }

  start(): void {
    const context = this.getContext();
    const preload = () =>
      this.preloads.sync(context.config.current, context.resourceBasePath, (id, error) =>
        this.reportError(id, error),
      );
    this.stops.push(
      context.config.subscribe(preload),
      context.gateway.subscribe(() => this.syncConnection()),
      context.gateway.subscribeEvents((event) => {
        if (event.event === "plugins.controlUi.changed" || event.event === "plugins.changed") {
          void this.refresh();
        }
      }),
    );
    preload();
    this.syncConnection();
  }

  private syncConnection(): void {
    const snapshot = this.getContext().gateway.snapshot;
    const client = snapshot.phase === "connected" ? snapshot.client : null;
    // Plugin generations can change while the server connection and UI owners survive.
    const connectionId = client ? (snapshot.hello?.server?.connId ?? null) : null;
    if (this.client === client && this.connectionId === connectionId) {
      return;
    }
    this.retireOwners();
    this.client = client;
    this.connectionId = connectionId;
    this.diagnostics = [];
    this.catalogStatus =
      client && !isGatewayMethodAdvertised(snapshot, "plugins.controlUi.list")
        ? "complete"
        : "pending";
    this.publish();
    if (client && isGatewayMethodAdvertised(snapshot, "plugins.controlUi.list")) {
      const reuseBootstrap = this.firstConnection;
      this.firstConnection = false;
      void this.refresh(reuseBootstrap);
    }
  }

  async refresh(reuseBootstrap = false): Promise<void> {
    const client = this.client;
    if (!client || this.disposed) {
      return;
    }
    const generation = ++this.refreshGeneration;
    // Explicit reload supersedes pending activation even when a plugin's async
    // initializer never settles; retained host closures are revoked immediately.
    for (const owner of this.loadingOwners) {
      this.disposeOwner(owner);
    }
    this.loadingOwners.clear();
    this.catalogStatus = "pending";
    this.loadingCatalog = "pending";
    this.publish();
    const current = () =>
      !this.disposed && this.client === client && this.refreshGeneration === generation;
    try {
      const sameOrigin = hasSameOriginGatewayTransport(client.gatewayUrl);
      const [catalog, bootstrap] = await Promise.all([
        client.request<PluginsControlUiCatalog>("plugins.controlUi.list", {}),
        sameOrigin ? this.getContext().config.refresh({ ifNeeded: reuseBootstrap }) : null,
      ]);
      if (!current()) {
        return;
      }
      this.diagnostics = catalog.diagnostics;
      this.catalogStatus = "complete";
      const installed = new Set(catalog.plugins.map((plugin) => plugin.pluginId));
      this.loadingCatalog = installed;
      for (const [id, owner] of this.owners) {
        const next = catalog.plugins.find((descriptor) => descriptor.pluginId === id);
        if (
          !next ||
          (owner.deliveryIdentity ?? "gateway") !== controlUiPluginDeliveryIdentity(next)
        ) {
          this.owners.delete(id);
          this.disposeOwner(owner);
        }
      }
      this.publish();
      if (catalog.plugins.length) {
        const gatewayUrl = new URL(client.gatewayUrl, window.location.href);
        gatewayUrl.protocol = gatewayUrl.protocol.replace(/^ws/u, "http");
        for (const descriptor of catalog.plugins) {
          if (controlUiPluginDistribution(descriptor)) {
            continue;
          }
          const grantError = !sameOrigin
            ? `Native plugin UI requires the Control UI served by the connected Gateway. Open ${gatewayUrl.origin} and reconnect there.`
            : !bootstrap
              ? "Could not authenticate native plugin assets. Reconnect and retry."
              : controlUiPluginAssetGrantError(
                  descriptor,
                  bootstrap,
                  this.getContext().resourceBasePath,
                );
          if (grantError) {
            installed.delete(descriptor.pluginId);
            const error = new Error(grantError);
            this.reportError(descriptor.pluginId, error);
            await this.reportActivation(descriptor, client, current, "failed", error);
            if (!current()) {
              return;
            }
          }
        }
        if (bootstrap?.pluginAssetsRequireAuth) {
          this.startGrantRenewal();
        }
      }
      // Each plugin owns its deadline. A slow initializer must not prevent
      // unrelated plugins from becoming available or keep Reload pending.
      const activations = catalog.plugins
        .filter(
          (descriptor) =>
            installed.has(descriptor.pluginId) &&
            this.owners.get(descriptor.pluginId)?.descriptor.revision !== descriptor.revision,
        )
        .map((descriptor) => this.activate(descriptor, client, current));
      // Loading becomes activation-owned before awaiting peers, so a ready or
      // failed page never waits for another plugin's initializer.
      this.loadingCatalog = null;
      this.publish();
      await Promise.all(activations);
      this.publish();
    } catch (error) {
      if (current()) {
        if (this.loadingCatalog === "pending") {
          this.catalogStatus = "failed";
        }
        this.loadingCatalog = null;
        this.reportError("host", error);
        this.publish();
      }
    }
  }

  private startGrantRenewal(): void {
    if (this.grantTimer !== null) {
      return;
    }
    this.grantTimer = setInterval(() => {
      if (this.client && this.owners.size) {
        void this.getContext()
          .config.refresh()
          .catch((error: unknown) => this.reportError("host", error));
      }
    }, CONTROL_UI_PLUGIN_AUTH_GRANT_TTL_MS / 2);
  }

  private async activate(
    descriptor: PluginControlUiModule,
    client: GatewayBrowserClient,
    current: () => boolean,
  ) {
    const abort = new AbortController();
    const owner: Omit<ControlUiPluginOwner, "host"> = {
      descriptor,
      deliveryIdentity: controlUiPluginDeliveryIdentity(descriptor),
      client,
      abort,
      disposers: new Set<ControlUiDisposer>(),
      contributions: {
        pages: new Map(),
        navigation: new Map(),
        panels: new Map(),
        actions: new Map(),
        replacements: new Map(),
        accessories: new Map(),
        widgets: new Map(),
      },
      selections: new Map<ControlUiSurface, string | null>(),
    };
    this.loadingOwners.add(owner);
    try {
      const styles: HTMLLinkElement[] = [];
      const initialize = async (): Promise<ControlUiPluginOwner | undefined> => {
        const { initializeControlUiPlugin } = await import("./control-ui-loader.ts");
        return initializeControlUiPlugin(this.getContext, this, owner, styles, () =>
          this.disposeOwner(owner),
        );
      };
      const complete = await raceWithTimeout(
        racePromiseWithAbortSignal(
          initialize(),
          abort.signal,
          () => new Error("Plugin UI activation ended."),
        ),
        ACTIVATION_TIMEOUT_MS,
        () => {
          throw new Error(
            "Plugin UI initialization timed out. Check the plugin and reload its UI.",
          );
        },
      );
      if (!complete || !current() || abort.signal.aborted) {
        this.disposeOwner(owner);
        return;
      }
      // Validate the whole staged selection before retiring a working activation.
      // A rejected revision must leave both its predecessor and the current choices intact.
      const selections = [...complete.selections].map(([surface, id]) => {
        if (id === null) {
          return [surface, null] as const;
        }
        const replacement = complete.contributions.replacements.get(id);
        if (replacement?.value.surface !== surface) {
          throw new Error("The selected UI replacement is unavailable.");
        }
        return [
          surface,
          { key: `${descriptor.pluginId}/${id}`, signal: replacement.signal },
        ] as const;
      });
      const previous = this.owners.get(descriptor.pluginId);
      // The receipt is asynchronous, but publication completes activation.
      // A concurrent catalog refresh may revoke only owners still initializing.
      this.loadingOwners.delete(owner);
      this.owners.set(descriptor.pluginId, complete);
      // A successful revision can inherit a live choice. Retirement or reuse of
      // the same contribution ID alone must never resurrect a previous choice.
      if (previous) {
        for (const [surface, selection] of this.selected) {
          const id = selection.key.slice(descriptor.pluginId.length + 1);
          if (
            selection.signal.aborted ||
            selection.signal !== previous.contributions.replacements.get(id)?.signal
          ) {
            continue;
          }
          const replacement = complete.contributions.replacements.get(id);
          if (replacement?.value.surface === surface) {
            this.selected.set(surface, { key: selection.key, signal: replacement.signal });
          } else {
            this.selected.delete(surface);
          }
        }
      }
      for (const [surface, selection] of selections) {
        if (selection === null) {
          this.selected.delete(surface);
        } else {
          this.selected.set(surface, selection);
        }
      }
      for (const link of styles) {
        link.media = "all";
      }
      if (previous) {
        this.disposeOwner(previous);
      }
      this.publish();
      await this.reportActivation(descriptor, client, current, "activated");
    } catch (error) {
      this.loadingOwners.delete(owner);
      this.disposeOwner(owner);
      if (current()) {
        this.reportError(descriptor.pluginId, error);
        this.publish();
        await this.reportActivation(descriptor, client, current, "failed", error);
      }
    } finally {
      this.loadingOwners.delete(owner);
    }
  }

  private async reportActivation(
    descriptor: PluginControlUiModule,
    client: GatewayBrowserClient,
    current: () => boolean,
    status: "activated" | "failed",
    error?: unknown,
  ): Promise<void> {
    if (!current()) {
      return;
    }
    try {
      await client.request("plugins.controlUi.report", {
        pluginId: descriptor.pluginId,
        revision: descriptor.revision,
        status,
        ...(error === undefined
          ? {}
          : { error: formatUiError(error, "Plugin UI activation failed.").slice(0, 512) }),
      });
    } catch (failure) {
      if (current()) {
        this.reportError(descriptor.pluginId, failure);
      }
    }
  }

  isCurrent(owner: Omit<ControlUiPluginOwner, "host">): boolean {
    return !this.disposed && !owner.abort.signal.aborted && this.client === owner.client;
  }

  register<K extends keyof ControlUiContributions>(
    owner: Omit<ControlUiPluginOwner, "host">,
    kind: K,
    value: ControlUiContributions[K],
  ): ControlUiDisposer {
    if (!this.isCurrent(owner)) {
      throw new Error("This plugin UI activation has ended.");
    }
    const entries = owner.contributions[kind];
    // A disposer owns the registered ID even if the plugin later mutates its definition.
    const id = value.id;
    if (!CONTRIBUTION_ID.test(id) || entries.has(id)) {
      throw new Error(`Invalid or duplicate plugin UI contribution: ${id}`);
    }
    const abort = new AbortController();
    const entry = { value, signal: AbortSignal.any([owner.abort.signal, abort.signal]) };
    entries.set(id, entry);
    const dispose = () => {
      // Reusing the same definition must not revive an earlier disposer.
      if (!owner.disposers.delete(dispose)) {
        return;
      }
      entries.delete(id);
      if (kind === "replacements") {
        for (const [surface, selectedId] of owner.selections) {
          if (selectedId === id) {
            owner.selections.delete(surface);
          }
        }
        this.clearSelections(entry.signal);
      }
      abort.abort();
      this.publish();
    };
    owner.disposers.add(dispose);
    // Listeners may retire or replace the activation during this publication.
    // Its registration and cleanup must already have the same owner.
    this.publish();
    return dispose;
  }

  registrations<K extends keyof ControlUiContributions>(
    kind: K,
  ): ControlUiRegistration<ControlUiContributions[K]>[] {
    const values: ControlUiRegistration<ControlUiContributions[K]>[] = [];
    for (const owner of this.owners.values()) {
      for (const [id, entry] of owner.contributions[kind]) {
        values.push({
          key: `${owner.descriptor.pluginId}/${id}`,
          pluginId: owner.descriptor.pluginId,
          value: entry.value,
          host: owner.host,
          signal: entry.signal,
        });
      }
    }
    return values.toSorted((a, b) => a.key.localeCompare(b.key));
  }

  selectedReplacement(
    surface: ControlUiSurface,
  ): ControlUiRegistration<ControlUiReplacement> | undefined {
    const selected = this.selected.get(surface);
    return this.registrations("replacements").find(
      (entry) =>
        entry.key === selected?.key &&
        entry.signal === selected.signal &&
        entry.value.surface === surface,
    );
  }

  selectReplacement(surface: ControlUiSurface, key: string | null): void {
    if (key === null) {
      this.selected.delete(surface);
    } else {
      const replacement = this.registrations("replacements").find(
        (entry) => entry.key === key && entry.value.surface === surface,
      );
      if (!replacement) {
        throw new Error("The selected UI replacement is unavailable.");
      }
      this.selected.set(surface, { key, signal: replacement.signal });
    }
    this.publish();
  }

  reportError(pluginId: string, error: unknown): void {
    const message = formatUiError(error, "Plugin UI failed.");
    if (
      this.diagnostics.some((entry) => entry.pluginId === pluginId && entry.message === message)
    ) {
      return;
    }
    this.diagnostics = [
      ...this.diagnostics.filter((entry) => entry.pluginId !== pluginId),
      {
        pluginId,
        message,
      },
    ].slice(-20);
    this.publish();
  }

  private publish(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private disposeOwner(owner: Omit<ControlUiPluginOwner, "host">): void {
    for (const { signal } of owner.contributions.replacements.values()) {
      this.clearSelections(signal);
    }
    owner.abort.abort();
    for (const dispose of [...owner.disposers].toReversed()) {
      try {
        dispose();
      } catch (error) {
        this.reportError(owner.descriptor.pluginId, error);
      }
    }
    owner.disposers.clear();
  }

  private clearSelections(signal: AbortSignal): void {
    for (const [surface, selection] of this.selected) {
      if (selection.signal === signal) {
        this.selected.delete(surface);
      }
    }
  }

  private retireOwners(): void {
    this.refreshGeneration += 1;
    this.catalogStatus = "pending";
    this.loadingCatalog = null;
    if (this.grantTimer !== null) {
      clearInterval(this.grantTimer);
      this.grantTimer = null;
    }
    const owners = [...this.owners.values(), ...this.loadingOwners];
    this.owners.clear();
    this.loadingOwners.clear();
    for (const owner of owners) {
      this.disposeOwner(owner);
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const owner of this.settingsLifetimes) {
      owner.abort();
    }
    this.preloads.dispose();
    this.retireOwners();
    for (const stop of this.stops) {
      stop();
    }
    this.stops.length = 0;
    this.listeners.clear();
  }
}
